import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  fetchFormFieldMetadataAPI,
  fetchCostCentersAPI,
  fetchCountriesAPI,
  fetchAuditLogsAPI,
  fetchFpnProductTypesAPI,
} from '../../services/api/commonService';
import {
  FormField,
  FormFieldMetadataResponse,
  FormFieldOption,
  CostCenter,
  Country,
  FpnProductType,
} from '../../types/common/api.types';
import { AuditLogEntry } from '../../types/audit-logs/auditLogs.types';

/**
 * Common Slice
 * Manages generic project-level data that is shared across the application
 * This includes form field metadata, lookup data, and other shared resources
 */

// ============================================================================
// State Interfaces
// ============================================================================

interface LoadingState {
  formFieldMetadata: boolean;
  costCenters: boolean;
  countries: boolean;
  auditLogs: boolean;
  fpnProductTypes: boolean;
}

interface CommonSliceState {
  /**
   * Form field metadata containing field definitions and options
   * Normalized by field name for efficient lookups
   */
  formFieldMetadata: Record<string, FormField>;

  /**
   * Quick access to field options by field name
   * Provides efficient option lookups for form components
   */
  fieldOptions: Record<string, FormFieldOption[]>;

  /**
   * Loading states for async operations
   */
  loading: LoadingState;

  /**
   * Error state for async operations
   */
  error: string | null;

  /**
   * Flag indicating if form field metadata has been loaded
   * Used to prevent unnecessary re-fetches
   */
  isFormFieldMetadataLoaded: boolean;

  /**
   * Cost centers data
   */
  costCenters: CostCenter[];

  /**
   * Flag indicating if cost centers have been loaded
   * Used to prevent unnecessary re-fetches
   */
  isCostCentersLoaded: boolean;

  /**
   * Countries data
   */
  countries: Country[];

  /**
   * Flag indicating if countries have been loaded
   * Used to prevent unnecessary re-fetches
   */
  isCountriesLoaded: boolean;

  /**
   * Audit logs data
   */
  auditLogs: AuditLogEntry[];

  /**
   * Flag indicating if audit logs have been loaded
   * Used to prevent unnecessary re-fetches
   */
  isAuditLogsLoaded: boolean;

  /**
   * Map of FPN id -> its PRODUCT_TYPE value(s). An FPN may have more than one product type, so
   * values are stored as an array. Used to classify FPNs (DEVICE / ACCESSORY / N/A) for the
   * Compatible Devices dropdown filter without per-FPN attribute fetches.
   */
  fpnProductTypes: Record<number, string[]>;

  /**
   * Flag indicating whether a fetch for FPN product types has been attempted (success or fail).
   * Used to prevent unnecessary re-fetches. (We use "attempted" rather than "loaded" because the
   * fetch fails open to an empty result when the backend query is not yet deployed.)
   */
  hasAttemptedFpnProductTypesFetch: boolean;
}

// ============================================================================
// Initial State
// ============================================================================

const initialState: CommonSliceState = {
  formFieldMetadata: {},
  fieldOptions: {},
  loading: {
    formFieldMetadata: false,
    costCenters: false,
    countries: false,
    auditLogs: false,
    fpnProductTypes: false,
  },
  error: null,
  isFormFieldMetadataLoaded: false,
  costCenters: [],
  isCostCentersLoaded: false,
  countries: [],
  isCountriesLoaded: false,
  auditLogs: [],
  isAuditLogsLoaded: false,
  fpnProductTypes: {},
  hasAttemptedFpnProductTypesFetch: false,
};

// ============================================================================
// Async Thunks
// ============================================================================

/**
 * Fetches form field metadata from the API
 * This includes field names, labels, and available options for form fields
 */
export const fetchFormFieldMetadata = createAsyncThunk(
  'common/fetchFormFieldMetadata',
  async (_, { rejectWithValue }) => {
    try {
      const data = await fetchFormFieldMetadataAPI();
      return data;
    } catch (error) {
      return rejectWithValue((error as Error).message || 'Failed to fetch form field metadata');
    }
  }
);

/**
 * Fetches cost centers from the API
 * This includes cost center codes and names
 */
export const fetchCostCenters = createAsyncThunk('common/fetchCostCenters', async (_, { rejectWithValue }) => {
  try {
    const data = await fetchCostCentersAPI();
    return data;
  } catch (error) {
    return rejectWithValue((error as Error).message || 'Failed to fetch cost centers');
  }
});

/**
 * Fetches countries from the API
 * This includes country names and codes
 */
export const fetchCountries = createAsyncThunk('common/fetchCountries', async (_, { rejectWithValue }) => {
  try {
    const data = await fetchCountriesAPI();
    return data;
  } catch (error) {
    return rejectWithValue((error as Error).message || 'Failed to fetch countries');
  }
});

/**
 * Fetches PRODUCT_TYPE classification for every FPN (single global call).
 * The underlying API fails open (returns []) if the backend query isn't deployed yet.
 */
export const fetchFpnProductTypes = createAsyncThunk(
  'common/fetchFpnProductTypes',
  async (_, { rejectWithValue }) => {
    try {
      const data = await fetchFpnProductTypesAPI();
      return data;
    } catch (error) {
      return rejectWithValue((error as Error).message || 'Failed to fetch FPN product types');
    }
  }
);

/**
 * Fetches audit logs from the API
 * This includes audit trail of system operations
 */
export const fetchAuditLogs = createAsyncThunk('common/fetchAuditLogs', async (_, { rejectWithValue }) => {
  try {
    const data = await fetchAuditLogsAPI();
    return data;
  } catch (error) {
    return rejectWithValue((error as Error).message || 'Failed to fetch audit logs');
  }
});

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Normalizes form field metadata by field name for efficient lookups
 * @param fields - Array of form fields from API response
 * @returns Record with field names as keys and FormField objects as values
 */
const normalizeFormFieldMetadata = (fields: FormField[]): Record<string, FormField> => {
  return fields.reduce(
    (acc, field) => {
      acc[field.fieldName] = field;
      return acc;
    },
    {} as Record<string, FormField>
  );
};

/**
 * Extracts field options from form fields for quick access
 * @param fields - Array of form fields from API response
 * @returns Record with field names as keys and option arrays as values
 */
const extractFieldOptions = (fields: FormField[]): Record<string, FormFieldOption[]> => {
  return fields.reduce(
    (acc, field) => {
      acc[field.fieldName] = field.options;
      return acc;
    },
    {} as Record<string, FormFieldOption[]>
  );
};

// ============================================================================
// Slice Definition
// ============================================================================

const commonSlice = createSlice({
  name: 'common',
  initialState,
  reducers: {
    /**
     * Clears error state
     */
    clearError: (state) => {
      state.error = null;
    },

    /**
     * Resets the slice to initial state
     */
    reset: () => initialState,
  },
  extraReducers: (builder) => {
    // ========================================================================
    // fetchFormFieldMetadata
    // ========================================================================
    builder
      .addCase(fetchFormFieldMetadata.pending, (state) => {
        state.loading.formFieldMetadata = true;
        state.error = null;
      })
      .addCase(fetchFormFieldMetadata.fulfilled, (state, action) => {
        state.loading.formFieldMetadata = false;
        const response = action.payload as FormFieldMetadataResponse;
        const fields = response?.fields ?? [];

        // Normalize and store form field metadata
        state.formFieldMetadata = normalizeFormFieldMetadata(fields);
        state.fieldOptions = extractFieldOptions(fields);
        state.isFormFieldMetadataLoaded = true;
      })
      .addCase(fetchFormFieldMetadata.rejected, (state, action) => {
        state.loading.formFieldMetadata = false;
        state.error = action.payload as string;
      })
      // ========================================================================
      // fetchCostCenters
      // ========================================================================
      .addCase(fetchCostCenters.pending, (state) => {
        state.loading.costCenters = true;
        state.error = null;
      })
      .addCase(fetchCostCenters.fulfilled, (state, action) => {
        state.loading.costCenters = false;
        state.costCenters = action.payload;
        state.isCostCentersLoaded = true;
      })
      .addCase(fetchCostCenters.rejected, (state, action) => {
        state.loading.costCenters = false;
        state.error = action.payload as string;
      })
      // ========================================================================
      // fetchCountries
      // ========================================================================
      .addCase(fetchCountries.pending, (state) => {
        state.loading.countries = true;
        state.error = null;
      })
      .addCase(fetchCountries.fulfilled, (state, action) => {
        state.loading.countries = false;
        state.countries = action.payload;
        state.isCountriesLoaded = true;
      })
      .addCase(fetchCountries.rejected, (state, action) => {
        state.loading.countries = false;
        state.error = action.payload as string;
      })
      // ========================================================================
      // fetchFpnProductTypes
      // ========================================================================
      .addCase(fetchFpnProductTypes.pending, (state) => {
        state.loading.fpnProductTypes = true;
      })
      .addCase(fetchFpnProductTypes.fulfilled, (state, action) => {
        state.loading.fpnProductTypes = false;
        state.hasAttemptedFpnProductTypesFetch = true;
        // Aggregate rows by fpn_id; an FPN may have multiple PRODUCT_TYPE values.
        const map: Record<number, string[]> = {};
        const payload = action.payload as FpnProductType[];
        payload.forEach(({ fpn_id, product_type }) => {
          if (fpn_id == null || !product_type) {
            return;
          }
          if (!map[fpn_id]) {
            map[fpn_id] = [];
          }
          if (!map[fpn_id].includes(product_type)) {
            map[fpn_id].push(product_type);
          }
        });
        state.fpnProductTypes = map;
      })
      .addCase(fetchFpnProductTypes.rejected, (state, action) => {
        state.loading.fpnProductTypes = false;
        // Mark attempted so we don't refetch in a loop; consumers fall back to "no classification".
        state.hasAttemptedFpnProductTypesFetch = true;
        state.error = action.payload as string;
      })
      // ========================================================================
      // fetchAuditLogs
      // ========================================================================
      .addCase(fetchAuditLogs.pending, (state) => {
        state.loading.auditLogs = true;
        state.error = null;
      })
      .addCase(fetchAuditLogs.fulfilled, (state, action) => {
        state.loading.auditLogs = false;
        state.auditLogs = action.payload;
        state.isAuditLogsLoaded = true;
      })
      .addCase(fetchAuditLogs.rejected, (state, action) => {
        state.loading.auditLogs = false;
        state.error = action.payload as string;
      });
  },
});

// ============================================================================
// Exports
// ============================================================================

export const { clearError, reset } = commonSlice.actions;

export default commonSlice.reducer;
